import { ChevronDown, MoonStar, Sunrise } from "lucide-react";
import { useState } from "react";
import { TimeField } from "@/components/TimeField";
import { formatDuration, minutesSinceMidnight } from "@/lib/clock";
import type { SleepLogConfig, SleepLogDayData } from "@/lib/schema";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";
import { evaluateHalf, type HalfResult, scoreDay } from "./scoring";
import { sleepDurationMinutes } from "./time";

type Props = WidgetDayViewProps<SleepLogConfig>;

const PREVIEW_DATA: SleepLogDayData = { asleepAt: "00:16", awokeAt: "06:45" };

function describeLateness(result: HalfResult): string {
  if (!result.logged) return "ikke logget";
  if (result.lateMinutes === null) return "tidlig";
  if (result.lateMinutes === 0) return "presis";
  return `${formatDuration(result.lateMinutes)} sent`;
}

export function DayView({
  config,
  data,
  dayKey,
  onChangeDay,
  readOnly = false,
}: Props) {
  const realData = (data ?? {}) as SleepLogDayData;
  const hasAny =
    realData.asleepAt !== undefined || realData.awokeAt !== undefined;
  const showPreview = readOnly && !hasAny;
  const shown = showPreview ? PREVIEW_DATA : realData;

  const asleep = evaluateHalf(config, config.asleepTarget, shown.asleepAt);
  const awake = evaluateHalf(config, config.awakeTarget, shown.awokeAt);
  const score = scoreDay(config, shown);

  const asleepMinutes = minutesSinceMidnight(shown.asleepAt);
  const awakeMinutes = minutesSinceMidnight(shown.awokeAt);
  const duration =
    asleepMinutes !== null && awakeMinutes !== null
      ? sleepDurationMinutes(asleepMinutes, awakeMinutes)
      : null;

  const writeTimes = (patch: Partial<SleepLogDayData>) => {
    if (readOnly) return;
    onChangeDay({ ...realData, ...patch } satisfies SleepLogDayData);
  };

  // True while focus sits in one of the time fields.
  const [editing, setEditing] = useState(false);

  // Ferdiglogget (CONTEXT.md): both times entered. There is no "ingen data"
  // escape — a night the watch missed simply stays blank at 0p.
  //
  // Deferred until focus leaves the fields. The draft commits on every
  // keystroke that parses and a lone "1" already parses (to 01:00), so
  // without this a half-typed time turns the card green and collapses it
  // mid-word. The score still updates live — only this presentation waits.
  const loggingCompleted =
    !readOnly &&
    !editing &&
    realData.asleepAt !== undefined &&
    realData.awokeAt !== undefined;

  // Not ready while a time field has focus: `loggingCompleted` is deferred
  // past editing, so a focus round-trip flickers it false and back. That is
  // not the night becoming logged, and treating it as one would fold the card
  // away between two corrections.
  const { open, toggle } = useAutoCollapse(dayKey, loggingCompleted, !editing);
  const collapsible = loggingCompleted;
  const collapsed = collapsible && !open;

  const rows: Array<{
    key: "asleepAt" | "awokeAt";
    label: string;
    icon: typeof MoonStar;
    value: string | undefined;
    result: HalfResult;
    testId: string;
  }> = [
    {
      key: "asleepAt",
      label: "Sovnet",
      icon: MoonStar,
      value: shown.asleepAt,
      result: asleep,
      testId: "asleep",
    },
    {
      key: "awokeAt",
      label: "Våknet",
      icon: Sunrise,
      value: shown.awokeAt,
      result: awake,
      testId: "awake",
    },
  ];

  return (
    <section
      className={`widget-card sleep-log${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        <div className="widget-card-head-right">
          <span
            className="widget-card-score"
            data-testid={`widget-score-${config.id}`}
          >
            <b>{score}</b>
            <span className="max"> / {2 * config.points}p</span>
          </span>
          {collapsible ? (
            <button
              type="button"
              className="widget-card-toggle"
              onClick={toggle}
              aria-label={collapsed ? "Utvid" : "Skjul"}
              aria-expanded={!collapsed}
              data-testid={`widget-collapse-${config.id}`}
            >
              <ChevronDown className="chev" size={16} />
            </button>
          ) : null}
        </div>
      </header>
      {collapsed ? (
        <div
          className="widget-card-summary"
          data-testid={`widget-summary-${config.id}`}
        >
          <span className="summary-line">
            <MoonStar className="summary-icon" size={14} aria-hidden="true" />
            {realData.asleepAt} → {realData.awokeAt}
            {duration !== null ? ` · ${formatDuration(duration)}` : ""}
          </span>
        </div>
      ) : null}
      <div className="widget-card-body">
        <div className="widget-card-submeta">
          Mål {config.asleepTarget} og {config.awakeTarget} · {config.points}p
          hver · −{config.reductionPerInterval}p per {config.intervalMinutes}{" "}
          min
        </div>
        {/* Focus is tracked on the group, not per field, so tabbing from
            Sovnet to Våknet does not read as leaving — the relatedTarget
            check is what distinguishes the two. */}
        <div
          className="sleep-rows"
          onFocus={() => setEditing(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setEditing(false);
            }
          }}
        >
          {rows.map((row) => {
            const Icon = row.icon;
            return (
              <div
                key={row.key}
                className="sleep-row"
                data-testid={`sleep-log-row-${row.testId}-${config.id}`}
              >
                <label
                  className="sleep-row-label"
                  htmlFor={`${row.key}-${config.id}`}
                >
                  <Icon size={14} aria-hidden="true" />
                  {row.label}
                </label>
                <TimeField
                  id={`${row.key}-${config.id}`}
                  value={row.value}
                  disabled={readOnly}
                  onChange={(next) => writeTimes({ [row.key]: next })}
                  data-testid={`sleep-log-${row.testId}-input-${config.id}`}
                />
                <span
                  className="sleep-row-note"
                  data-testid={`sleep-log-${row.testId}-note-${config.id}`}
                >
                  {describeLateness(row.result)}
                </span>
                <span
                  className="sleep-row-score"
                  data-testid={`sleep-log-${row.testId}-score-${config.id}`}
                >
                  {row.result.points}p
                </span>
              </div>
            );
          })}
        </div>
        {/* Duration is shown, never scored (CONTEXT.md: "Sovetid"). */}
        <div
          className="sleep-duration"
          data-testid={`sleep-log-duration-${config.id}`}
        >
          {duration !== null ? (
            <>
              Sov <b>{formatDuration(duration)}</b>
            </>
          ) : (
            "Sovetid vises når begge tidene er fylt ut."
          )}
        </div>
      </div>
    </section>
  );
}
